import { useRef, useState } from 'react';
import { useUploadQrLogo } from '../api/useUploadQrLogo';

interface Props {
  logoUrl: string | null;
  onChange: (next: { s3_key: string; preview_url: string } | null) => void;
}

export function LogoUploader({ logoUrl, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { upload, loading } = useUploadQrLogo();
  const [error, setError] = useState<string | null>(null);

  const handlePick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    const result = await upload(file);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    if (result.s3_key && result.preview_url) {
      onChange({ s3_key: result.s3_key, preview_url: result.preview_url });
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml"
        onChange={handleFile}
        className="hidden"
      />
      {logoUrl ? (
        <div className="flex items-center gap-3 p-2 rounded-md border border-gray-800 bg-gray-900/40">
          <div className="w-12 h-12 rounded bg-white flex items-center justify-center overflow-hidden">
            <img src={logoUrl} alt="Logo preview" className="max-w-full max-h-full object-contain" />
          </div>
          <div className="flex-1 text-xs text-gray-400">
            Logo uploaded · displayed at the center of the QR
          </div>
          <button
            type="button"
            onClick={handlePick}
            className="px-2 py-1 text-xs rounded border border-gray-800 text-gray-300 hover:bg-gray-800/50"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="px-2 py-1 text-xs rounded text-red-400 hover:bg-red-950/30"
          >
            Remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handlePick}
          disabled={loading}
          className="w-full p-3 rounded-md border border-dashed border-gray-700 bg-gray-900/40 text-xs text-gray-400 hover:border-gray-600 hover:text-gray-300 disabled:opacity-50"
        >
          {loading ? 'Uploading…' : 'Upload logo · PNG / JPG / SVG · ≤500KB · ≤1024×1024px'}
        </button>
      )}

      {error && (
        <div className="text-[11px] text-red-400">{error}</div>
      )}
    </div>
  );
}
